import React, { useState, useEffect, useCallback } from "react";
import { Box, Typography, Paper, Button, CircularProgress } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useParams, useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";
import { useNotification } from "../../../providers/NotificationProvider";
import { BSC_CATEGORY_CONFIG } from "./bscConfig";

const formatDate = (iso) => {
  if (!iso) return "—";
  return iso.substring(0, 10).replace(/-/g, "/");
};

function DetailRow({ label, value }) {
  return (
    <Box
      sx={{
        display: "flex",
        py: 1.5,
        borderBottom: "1px solid",
        borderColor: "divider",
      }}
    >
      <Typography
        variant="body2"
        sx={{ width: 200, flexShrink: 0, color: "text.secondary", fontWeight: 500 }}
      >
        {label}
      </Typography>
      <Typography variant="body2">{value || "—"}</Typography>
    </Box>
  );
}

export default function BscGoalDetailPage() {
  const { businessDomainId, businessUnitId, category, goalIdx } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const notify = useNotification();
  const t = (id) => intl.formatMessage({ id });
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(false);

  const config = BSC_CATEGORY_CONFIG[category];
  const basePath = `/strategy/${businessDomainId}/business-units/${businessUnitId}/bsc/${category}`;

  const loadData = useCallback(async () => {
    if (!config) return;
    setLoading(true);
    try {
      const data = await config.fetchGoals(businessUnitId);
      const arr = Array.isArray(data) ? data : [];
      setItem(arr[Number(goalIdx)] || null);
    } catch (e) {
      notify.apiError(e);
    } finally {
      setLoading(false);
    }
  }, [businessUnitId, category, goalIdx]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    loadData();
  }, [loadData]);

  if (loading)
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 6 }}>
        <CircularProgress />
      </Box>
    );
  if (!item) return null;

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", mb: 3, gap: 2 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(basePath)}
          variant="text"
        >
          {t("common.back")}
        </Button>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          {config ? t(config.titleKey) : category}
        </Typography>
      </Box>
      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <DetailRow label={t("bsc.successFactor")} value={item.successFactorName} />
        <DetailRow
          label={t("bsc.indicator")}
          value={
            item.indicator
              ? `${item.indicator.indicatorName || ""}${item.indicator.unit ? ` (${item.indicator.unit})` : ""}`
              : null
          }
        />
        <DetailRow label={t("bsc.unit")} value={item.indicator?.unit} />
        <DetailRow label={t("bsc.plannedValue")} value={item.goal?.plannedValue != null ? String(item.goal.plannedValue) : null} />
        <DetailRow label={t("bsc.plannedDate")} value={formatDate(item.goal?.plannedDate)} />
        <DetailRow label={t("bsc.deadline")} value={formatDate(item.goal?.deadline)} />
      </Paper>
      <Button
        variant="outlined"
        onClick={() => navigate(`${basePath}/${goalIdx}/achievements`)}
      >
        {t("bsc.achievement.list.title")}
      </Button>
    </Box>
  );
}
